/**
 * DrillPlanCreator — template-driven drill plan builder (MVP automated driller).
 *
 * Flow:
 *  1. TemplatePicker: user picks a template (Positional Intro / Small Plan / Full)
 *  2. AutoFill: slots are filled per posture+measurement analysis
 *  3. Per-block override: SlotDrillPicker opens on slot tap (minimized carousel by default)
 *  4. Save: writes to `drill-plans` collection and assigns to client
 *
 * @see docs/features/260331-automated-driller-mvp/FEATURE_SPEC.md
 */

import React, { useState, useMemo } from 'react';
import {
  Loader2,
  RotateCcw,
  Save,
  Wand2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Brain,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/context/client-context';
import type { Drill } from '@/components/drill-library/types/drill-types';
import {
  MVP_TEMPLATE_MAP,
  type DrillPlanTemplateDefinition,
  type DrillPlanSlotMeta,
} from './lib/program-structure';
import type { DrillPlanDay, DrillPlanSlot } from './types/drill-plan-types';
import { buildDrillPlanTags } from './types/drill-plan-types';
import { useAutoFill, type SlotSuggestions } from './hooks/use-auto-fill';
import type { DrillSuggestionFilters } from '@/lib/posture-analysis/drill-suggestion-filters';
import { deriveClientPain, clientDisplayLabel } from '@/lib/clients/roster-fields';
import TemplatePicker from './TemplatePicker';
import SlotDrillPicker from './SlotDrillPicker';
import { slotIdToCategory } from './lib/suggest-drills';
import { createDrillPlan, createDrillPlanAssignment } from './services/drill-plans-api';
import logger from '@/lib/logging';
import ClientRequiredGate from './ClientRequiredGate';

interface DrillPlanCreatorProps {
  drills: Drill[];
  onSaved?: (planId: string) => void;
  initialFilters?: DrillSuggestionFilters;
  initialTemplateId?: 'positional-intro' | 'small-drill-plan' | 'full';
}

/** Whether the user prefers posture-based or measurement-based auto-fill */
type SuggestionMode = 'posture' | 'ai';

const DrillPlanCreator: React.FC<DrillPlanCreatorProps> = ({
  drills,
  onSaved,
  initialFilters,
  initialTemplateId,
}) => {
  const { user } = useAuth();
  const { client } = useClient();
  const { toast } = useToast();
  const { autoFill, isLoading: isAutoFilling } = useAutoFill();

  const [template, setTemplate] = useState<DrillPlanTemplateDefinition | null>(
    initialTemplateId ? MVP_TEMPLATE_MAP[initialTemplateId] ?? null : null
  );
  const [planName, setPlanName] = useState('');
  const [days, setDays] = useState<DrillPlanDay[]>([]);
  const [filters, setFilters] = useState<DrillSuggestionFilters | null>(initialFilters ?? null);
  const [missedSlots, setMissedSlots] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Dual suggestion tracking
  const [slotSuggestions, setSlotSuggestions] = useState<Record<string, SlotSuggestions>>({});
  const [suggestionMode, setSuggestionMode] = useState<SuggestionMode>('posture');

  // Slot picker state
  const [pickerSlot, setPickerSlot] = useState<DrillPlanSlotMeta | null>(null);
  const [pickerDayIndex, setPickerDayIndex] = useState<number>(0);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Carousel collapse state — minimized by default per spec
  const [isCarouselExpanded, setIsCarouselExpanded] = useState(false);

  const clientPhase = useMemo((): 'P1' | 'P2' | 'P3' | null => {
    if (!client?.classification) return null;
    const m = client.classification.match(/P([123])/i);
    return m ? (`P${m[1]}` as 'P1' | 'P2' | 'P3') : null;
  }, [client]);

  const drillById = useMemo(() => {
    const map: Record<string, Drill> = {};
    for (const d of drills) map[d.id] = d;
    return map;
  }, [drills]);

  const handleSelectTemplate = async (tpl: DrillPlanTemplateDefinition) => {
    if (!client) return;
    setTemplate(tpl);
    setDays([]);
    setMissedSlots([]);
    setSlotSuggestions({});
    setIsCarouselExpanded(false); // always start collapsed

    if (!planName) setPlanName(`${clientDisplayLabel(client)} – ${tpl.name} Plan`);

    try {
      const result = await autoFill(tpl, client, drills);
      setDays(result.days);
      setFilters(result.filters);
      setMissedSlots(result.missedSlots);
      setSlotSuggestions(result.slotSuggestions);
      if (result.missedSlots.length > 0) {
        toast({
          title: 'Some slots need manual selection',
          description: `${result.missedSlots.length} slot(s) had no matching drills.`,
        });
      }
    } catch (err) {
      logger.error('drill-plan:auto-fill:error', err);
      toast({
        title: 'Auto-fill failed',
        description: 'You can fill slots manually.',
        variant: 'destructive',
      });
    }
  };

  /** Apply the chosen suggestion mode to all slots */
  const applyMode = (mode: SuggestionMode) => {
    setSuggestionMode(mode);
    if (!template || Object.keys(slotSuggestions).length === 0) return;

    setDays((prev) =>
      prev.map((day) => {
        const newSlots = { ...day.slots };
        for (const slotMeta of template.slots) {
          const sugg = slotSuggestions[slotMeta.id];
          if (!sugg) continue;
          const drill = mode === 'ai' ? sugg.measurementBased : sugg.postureBased;
          if (drill) {
            newSlots[slotMeta.id] = makeSlot(drill.id);
          }
        }
        return { ...day, slots: newSlots };
      })
    );
  };

  const handleSlotClick = (slotMeta: DrillPlanSlotMeta, dayIndex: number) => {
    setPickerSlot(slotMeta);
    setPickerDayIndex(dayIndex);
    setIsPickerOpen(true);
  };

  const handleSlotSelect = (drillId: string) => {
    if (!pickerSlot || !template) return;
    setDays((prev) =>
      prev.map((day, idx) => {
        if (idx !== pickerDayIndex) return day;
        return { ...day, slots: { ...day.slots, [pickerSlot.id]: makeSlot(drillId) } };
      })
    );
    setMissedSlots((prev) => prev.filter((s) => s !== pickerSlot.id));
  };

  const handleSave = async () => {
    if (!client || !template || days.length === 0 || !user?.uid) {
      toast({
        title: 'Cannot save',
        description: 'Please select a template and client before saving.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const phase = filters?.phaseCode ?? clientPhase ?? 'P1';
      const tags = buildDrillPlanTags(phase, client.liftingExperience, deriveClientPain(client).painStatus);
      const slotLabels: Record<string, string> = {};
      for (const slot of template.slots) slotLabels[slot.id] = slot.label;

      const planId = await createDrillPlan({
        name: planName || `${clientDisplayLabel(client)} – ${template.name} Plan`,
        templateId: template.id,
        phase,
        twiceDaily: template.twiceDaily,
        tags,
        days,
        slotLabels,
        suggestionFilterSnapshot: filters ?? undefined,
        createdBy: user.uid,
      });

      await createDrillPlanAssignment(client.id, {
        drillPlanDefinitionId: planId,
        clientId: client.id,
        assigningPractitionerUid: user.uid,
        drillPlanName: planName || `${clientDisplayLabel(client)} – ${template.name} Plan`,
      });

      logger.info('drill-plan-assignment:create', {
        planId,
        clientId: client.id,
        assignedByUid: user.uid,
      });

      toast({
        title: 'Drill plan saved!',
        description: `"${planName}" assigned to ${clientDisplayLabel(client)}.`,
      });

      onSaved?.(planId);
      setTemplate(null);
      setDays([]);
      setFilters(null);
      setPlanName('');
      setMissedSlots([]);
      setSlotSuggestions({});
    } catch (err) {
      logger.error('drill-plan:save:error', err);
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setTemplate(null);
    setDays([]);
    setFilters(null);
    setPlanName('');
    setMissedSlots([]);
    setSlotSuggestions({});
    setIsCarouselExpanded(false);
  };

  const hasDualSuggestions = Object.values(slotSuggestions).some(
    (s) => s.postureBased && s.measurementBased && s.postureBased.id !== s.measurementBased.id
  );

  return (
    <ClientRequiredGate>
      <div className="space-y-5 pb-safe">
        {/* Step 1: Template Picker */}
        {!template && (
          <div className="space-y-3">
            <h2 className="text-xs font-semibold text-muted uppercase tracking-wide px-1">
              Choose a Template
            </h2>
            <TemplatePicker
              clientPhase={clientPhase}
              onSelect={handleSelectTemplate}
              disabled={isAutoFilling}
            />
          </div>
        )}

        {/* Auto-fill loading */}
        {isAutoFilling && (
          <div className="flex items-center gap-3 text-muted py-8 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Suggesting drills based on assessment…</span>
          </div>
        )}

        {/* Steps 2–3: Plan editor */}
        {template && !isAutoFilling && days.length > 0 && (
          <div className="space-y-4">
            {/* Header row */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="secondary" className="text-xs">{template.name}</Badge>
                {template.twiceDaily && (
                  <Badge variant="outline" className="text-xs">2×/day</Badge>
                )}
                {filters?.phaseTag && (
                  <Badge variant="outline" className="text-xs">{filters.phaseTag}</Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={handleReset} className="gap-1.5 h-8 text-xs">
                <RotateCcw className="h-3.5 w-3.5" />
                Change
              </Button>
            </div>

            {/* Dual-suggestion mode toggle — only shown when results differ */}
            {hasDualSuggestions && (
              <div className="rounded-lg border bg-fg/5 p-3 space-y-2">
                <p className="text-xs font-medium text-muted">
                  Two suggestion methods available — choose which to apply:
                </p>
                <Tabs
                  value={suggestionMode}
                  onValueChange={(v) => applyMode(v as SuggestionMode)}
                  className="w-full"
                >
                  <TabsList className="w-full h-9">
                    <TabsTrigger value="posture" className="flex-1 gap-1.5 text-xs">
                      <Sparkles className="h-3.5 w-3.5" />
                      Posture-based
                    </TabsTrigger>
                    <TabsTrigger value="ai" className="flex-1 gap-1.5 text-xs">
                      <Brain className="h-3.5 w-3.5" />
                      Worst measurement
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            )}

            {/* Plan name */}
            <div className="space-y-1">
              <Label htmlFor="plan-name" className="text-xs font-medium">Plan name</Label>
              <Input
                id="plan-name"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder={`${client ? clientDisplayLabel(client) : 'Client'} – ${template.name} Plan`}
                className="h-9 text-sm"
              />
            </div>

            <Separator />

            {/* Drill selection carousel — collapsible, minimized by default */}
            <div className="rounded-lg border overflow-hidden">
              <button
                onClick={() => setIsCarouselExpanded((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-fg/5 hover:bg-fg/5 transition-colors text-left"
              >
                <span className="text-xs font-medium">
                  Drill Selection
                  <span className="ml-1.5 text-muted font-normal">
                    (tap a slot to swap)
                  </span>
                </span>
                {isCarouselExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted" />
                )}
              </button>

              {isCarouselExpanded && (
                <div className="divide-y">
                  {days.map((day, dayIndex) => (
                    <div key={day.name} className="px-4 py-3 space-y-2">
                      <p className="text-xs font-semibold text-muted">{day.name}</p>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {template.slots.map((slotMeta) => {
                          const slot = day.slots[slotMeta.id];
                          const drill = slot ? drillById[slot.drillId] : undefined;
                          const isMissed = !slot && missedSlots.includes(slotMeta.id);
                          const sugg = slotSuggestions[slotMeta.id];
                          const altDrill =
                            suggestionMode === 'ai'
                              ? sugg?.postureBased
                              : sugg?.measurementBased;

                          return (
                            <button
                              key={slotMeta.id}
                              onClick={() => handleSlotClick(slotMeta, dayIndex)}
                              className={`text-left p-2.5 rounded-lg border transition-colors ${
                                drill
                                  ? 'border-line hover:border-accent/50 hover:bg-accent'
                                  : isMissed
                                  ? 'border-dashed border-muted-foreground/40 hover:border-accent/50'
                                  : 'border-dashed border-muted-foreground/20'
                              }`}
                            >
                              <div className="text-[10px] font-medium text-muted mb-0.5">
                                {slotMeta.label}
                              </div>
                              {drill ? (
                                <>
                                  <div className="text-xs font-medium truncate leading-snug">
                                    {drill.name}
                                  </div>
                                  {altDrill && altDrill.id !== drill.id && (
                                    <div className="text-[10px] text-muted mt-0.5 truncate">
                                      Alt: {altDrill.name}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex items-center gap-1 text-[10px] text-muted">
                                  <Wand2 className="h-3 w-3" />
                                  {isMissed ? 'No match' : 'Add drill'}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Main plan view (always visible) — focused on reps/order */}
            <div className="space-y-4">
              <h3 className="text-xs font-semibold text-muted uppercase tracking-wide">
                Plan Overview
              </h3>
              {days.map((day, dayIndex) => (
                <div key={day.name} className="space-y-2">
                  <p className="text-sm font-semibold">{day.name}</p>
                  <div className="space-y-1.5">
                    {template.slots.map((slotMeta, slotIdx) => {
                      const slot = day.slots[slotMeta.id];
                      const drill = slot ? drillById[slot.drillId] : undefined;
                      const isMissed = !slot && missedSlots.includes(slotMeta.id);

                      return (
                        <div
                          key={slotMeta.id}
                          className="flex items-center gap-3 p-2.5 rounded-lg bg-fg/5 border border-transparent hover:border-line transition-colors"
                        >
                          {/* Order number */}
                          <span className="w-5 h-5 rounded-full bg-fg/10 flex items-center justify-center text-[10px] font-bold text-muted flex-shrink-0">
                            {slotIdx + 1}
                          </span>

                          {/* Slot label + drill name */}
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] text-muted font-medium">
                              {slotMeta.label}
                            </div>
                            {drill ? (
                              <div className="text-sm font-medium truncate">{drill.name}</div>
                            ) : (
                              <div className="text-xs text-muted italic">
                                {isMissed ? 'No match — tap to pick' : 'Empty'}
                              </div>
                            )}
                          </div>

                          {/* Inline reps/sets inputs */}
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <input
                              type="text"
                              placeholder="Sets"
                              value={slot?.sets ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDays((prev) =>
                                  prev.map((d, di) =>
                                    di !== dayIndex
                                      ? d
                                      : {
                                          ...d,
                                          slots: {
                                            ...d.slots,
                                            [slotMeta.id]: {
                                              ...makeSlot(slot?.drillId ?? ''),
                                              ...(slot ?? {}),
                                              sets: val,
                                            },
                                          },
                                        }
                                  )
                                );
                              }}
                              className="w-12 h-7 rounded border border-line bg-bg px-1.5 text-center text-xs focus:outline-none focus:ring-1 focus:ring-accent"
                              aria-label="Sets"
                            />
                            <input
                              type="text"
                              placeholder="Reps"
                              value={slot?.reps ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setDays((prev) =>
                                  prev.map((d, di) =>
                                    di !== dayIndex
                                      ? d
                                      : {
                                          ...d,
                                          slots: {
                                            ...d.slots,
                                            [slotMeta.id]: {
                                              ...makeSlot(slot?.drillId ?? ''),
                                              ...(slot ?? {}),
                                              reps: val,
                                            },
                                          },
                                        }
                                  )
                                );
                              }}
                              className="w-12 h-7 rounded border border-line bg-bg px-1.5 text-center text-xs focus:outline-none focus:ring-1 focus:ring-accent"
                              aria-label="Reps"
                            />
                          </div>

                          {/* Tap to change drill */}
                          <button
                            onClick={() => handleSlotClick(slotMeta, dayIndex)}
                            className="text-muted hover:text-accent flex-shrink-0 p-1 rounded"
                            title="Change drill"
                          >
                            <Wand2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Save */}
            <div className="sticky bottom-0 bg-bg border-t pt-3 pb-safe flex justify-end">
              <Button onClick={handleSave} disabled={isSaving} className="gap-2 w-full sm:w-auto">
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {isSaving ? 'Saving…' : 'Save & Assign to Client'}
              </Button>
            </div>
          </div>
        )}

        {/* Empty state after template pick but before fill */}
        {template && !isAutoFilling && days.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted text-sm">
              Auto-fill produced no slots. Try a different template or check client data.
            </CardContent>
          </Card>
        )}

        {/* Slot drill picker dialog */}
        {pickerSlot && (
          <SlotDrillPicker
            isOpen={isPickerOpen}
            onClose={() => {
              setIsPickerOpen(false);
              setPickerSlot(null);
            }}
            slotLabel={pickerSlot.label}
            slotCategory={pickerSlot.category}
            currentDrillId={days[pickerDayIndex]?.slots[pickerSlot.id]?.drillId}
            filters={filters}
            drills={drills}
            slotSuggestions={slotSuggestions[pickerSlot.id] ?? null}
            onSelect={handleSlotSelect}
          />
        )}
      </div>
    </ClientRequiredGate>
  );
};

function makeSlot(drillId: string): DrillPlanSlot {
  return { drillId, sets: '', reps: '', rest: '', tempo: '', notes: '' };
}

export default DrillPlanCreator;
